import { useSetState } from "./use-set-state";
import { useQuery } from "@tanstack/react-query";

export const DEFAULT_LIMIT = 10;
export const limitSelections = [5, 10, 25, 50, 100];

const initialPagination = {
  pageSize: DEFAULT_LIMIT,
  pageIndex: 0,
};

export function usePaginationQuery(queryKey, queryFn) {
  const [{ pageIndex, pageSize }, setPagination] =
    useSetState(initialPagination);

  const query = useQuery({
    queryKey: [...queryKey, pageSize, pageIndex],
    queryFn: () => queryFn({ pageIndex, pageSize }),
    select: (data) => data?.data,
    keepPreviousData: true,
  });

  const queryData = {
    items: query.data?.data ?? [],
    meta: {
      total: query.data?.metadata?.total ?? 0,
      page: query.data?.metadata?.page ?? 1,
      pageCount: query.data?.metadata?.lastPage ?? 1,
      totalAll: query.data?.metadata?.totalAll ?? 0,
      totalPrice: query.data?.metadata?.totalPrice ?? 0,
      off: query.data?.metadata?.off ?? 0,
      on: query.data?.metadata?.on ?? 0,
    },
  };

  function prevPageHandler() {
    setPagination({ pageIndex: Math.max(0, pageIndex - 1) });
  }

  function nextPageHandler() {
    const hasMore = queryData.meta.page < queryData.meta.pageCount;

    if (!query.isPreviousData && hasMore) {
      setPagination({ pageIndex: pageIndex + 1 });
    }
  }

  function jumpToPageHandler(gotoPage) {
    setPagination({ pageIndex: gotoPage });
  }

  function changePageSizeHandler(nextPageSize) {
    setPagination({ pageSize: nextPageSize });
  }

  function getDataTableProps() {
    return {
      data: queryData.items,
      total: queryData.meta.total,
      pageCount: queryData.meta.pageCount,
      totalAll: queryData.meta.totalAll,
      totalPrice: queryData.meta.totalPrice,
      pagination: { pageIndex, pageSize },
      onPaginationChange: setPagination,
      off: queryData?.meta?.off,
      on: queryData?.meta?.on,
    };
  }

  return {
    ...query,
    data: queryData,
    isEmpty: queryData.items.length < 1,
    prevPageHandler,
    nextPageHandler,
    jumpToPageHandler,
    changePageSizeHandler,
    getDataTableProps,
  };
}
